import type { ReactNode } from 'react'
import { PexIcon } from '../icons/PexIcon'
import { Button } from '../ui/button'

export type MobileOperationsListLabels = {
  loading: string
  error: string
  retry: string
  empty: string
}

export type MobileOperationsListProps<T> = {
  items: T[]
  loading: boolean
  error: boolean
  getKey: (item: T) => string
  renderItem: (item: T) => ReactNode
  labels: MobileOperationsListLabels
  onRetry: () => void
}

function MobileOperationsSkeleton() {
  return (
    <div className="animate-pulse border-b border-[var(--color-border)] px-4 py-3">
      <div className="grid grid-cols-[28px_minmax(0,1fr)_auto] gap-3">
        <div className="mt-1 h-5 w-5 rounded border bg-[var(--color-surface-muted)]" />
        <div className="space-y-2">
          <div className="h-4 w-36 rounded bg-[var(--color-surface-muted)]" />
          <div className="h-3 w-48 rounded bg-[var(--color-surface-muted)]" />
          <div className="h-5 w-28 rounded bg-[var(--color-surface-muted)]" />
        </div>
        <div className="space-y-2 text-right">
          <div className="ml-auto h-4 w-16 rounded bg-[var(--color-surface-muted)]" />
          <div className="ml-auto h-3 w-12 rounded bg-[var(--color-surface-muted)]" />
        </div>
      </div>
    </div>
  )
}

export function MobileOperationsList<T>({
  items,
  loading,
  error,
  getKey,
  renderItem,
  labels,
  onRetry,
}: MobileOperationsListProps<T>) {
  if (loading) {
    return (
      <div aria-label={labels.loading}>
        <span className="sr-only">{labels.loading}</span>
        {Array.from({ length: 6 }, (_, index) => (
          <MobileOperationsSkeleton key={index} />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div
        role="alert"
        className="flex min-h-52 flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-danger)_9%,transparent)] text-[var(--color-danger)]">
          <PexIcon name="warning" size="md" decorative />
        </span>
        <p className="text-sm font-semibold">{labels.error}</p>
        <Button type="button" variant="outline" className="gap-2" onClick={onRetry}>
          <PexIcon name="refresh" size="sm" decorative />
          {labels.retry}
        </Button>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-52 flex-col items-center justify-center gap-3 px-6 text-center text-[var(--color-text-muted)]">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-surface-muted)]">
          <PexIcon name="inbox" size="md" decorative />
        </span>
        <p className="text-sm font-medium">{labels.empty}</p>
      </div>
    )
  }

  return (
    <div className="min-w-0 divide-y divide-[var(--color-border)] overflow-hidden bg-[var(--color-surface)]">
      {items.map((item) => (
        <div key={getKey(item)} className="min-w-0 overflow-hidden">
          {renderItem(item)}
        </div>
      ))}
    </div>
  )
}
